/**
 * 采购单浏览器打印（针式/多联纸）：新开窗口承载 HTML + 打印样式，避免 PDF 链路问题。
 * 依赖页面内已渲染的 #purchase-pdf-content（与 PurchasePdfPreviewDialog 一致）。
 */

function absolutizeImgSrc(root: HTMLElement) {
  root.querySelectorAll('img').forEach((img) => {
    if (!img.src || img.src.startsWith('data:')) return
    try {
      const a = document.createElement('a')
      a.href = img.getAttribute('src') || img.src
      img.setAttribute('src', a.href)
    } catch {
      /* ignore */
    }
  })
}

/** 打印专用样式：仅控制分页，具体视觉沿用当前页面（与下载 PDF 预览一致） */
const PRINT_CSS = `
@page { size: A4 portrait; margin: 8mm; }
html, body { margin: 0; padding: 0; }

/* 覆盖预览样式中的固定高度/大外边距，否则会导致“每页后多一张空白页” */
@media print {
  .purchase-pdf-page {
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
    page-break-after: always;
    break-after: page;
  }
  .purchase-pdf-page:nth-child(n+1) {
    margin-bottom: 0 !important;
  }
  .purchase-pdf-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
}
`

/** 验收单A4纵向打印专用样式 */
const ACCEPTANCE_PRINT_CSS = `
@page { size: A4 portrait; margin: 8mm; }
html, body { margin: 0; padding: 0; }

@media print {
  .acceptance-pdf-page {
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
    page-break-after: always;
    break-after: page;
  }
  .acceptance-pdf-page:nth-child(n+1) {
    margin-bottom: 0 !important;
  }
  .acceptance-pdf-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  /* 防止表格行被分页截断 */
  .acceptance-pdf-page tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
`

/** 横向A4列表打印专用样式 */
const LANDSCAPE_PRINT_CSS = `
@page { size: A4 landscape; margin: 8mm; }
html, body { margin: 0; padding: 0; }

@media print {
  .print-list-page {
    width: 100%;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
    page-break-after: always;
    break-after: page;
  }
  .print-list-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  /* 防止表格行被分页截断 */
  .print-list-page tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  /* 表头每页重复 */
  .print-list-page thead {
    display: table-header-group;
  }
}
`

function collectCurrentDocumentStyles(): string {
  return Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((node) => {
      const tag = node.tagName.toLowerCase()
      if (tag === 'style') return (node as HTMLStyleElement).outerHTML
      const link = node as HTMLLinkElement
      if (!link.href) return ''
      return `<link rel="stylesheet" href="${link.href}">`
    })
    .filter(Boolean)
    .join('\n')
}

function waitForImagesThenPrint(win: Window) {
  const doc = win.document
  const run = () => {
    win.focus()
    win.print()
    win.addEventListener('afterprint', () => {
      try {
        win.close()
      } catch {
        /* ignore */
      }
    }, { once: true })
  }
  const imgs = [...doc.images]
  if (imgs.length === 0) {
    setTimeout(run, 200)
    return
  }
  let left = imgs.length
  const done = () => {
    left--
    if (left <= 0) setTimeout(run, 150)
  }
  imgs.forEach((im) => {
    if (im.complete) done()
    else {
      im.addEventListener('load', done, { once: true })
      im.addEventListener('error', done, { once: true })
    }
  })
  setTimeout(run, 1500)
}

/** 避免 innerHTML 拼接时误闭合 script（备注等字段极少出现，防御性替换） */
function escapeRawHtmlForDocument(html: string): string {
  return html.replace(/<\/script>/gi, '<\\/script>')
}

/**
 * 克隆 #purchase-pdf-content 到新窗口并唤起系统打印对话框。
 * 使用 Blob URL 加载完整 HTML，避免 about:blank + document.write 在部分浏览器下白屏。
 */
export function printPurchaseOrderFromContentElement(source: HTMLElement): void {
  const clone = source.cloneNode(true) as HTMLElement
  absolutizeImgSrc(clone)

  const bodyHtml = escapeRawHtmlForDocument(clone.outerHTML)
  if (!bodyHtml.trim() || bodyHtml.length < 50) {
    throw new Error('打印区域为空，请关闭后重新打开预览再试')
  }

  const inheritedStyles = collectCurrentDocumentStyles()
  const fullHtml =
    '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    '<title>采购单打印</title>' +
    inheritedStyles +
    `<style>${PRINT_CSS}</style></head><body>${bodyHtml}</body></html>`

  const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' })
  const blobUrl = URL.createObjectURL(blob)

  // 勿使用 noopener：部分环境下会导致子窗口 document 无法正常展示 write/导航后的内容
  const w = window.open(blobUrl, '_blank', 'popup=yes,width=960,height=1200')
  if (!w) {
    URL.revokeObjectURL(blobUrl)
    throw new Error('无法打开打印窗口，请允许本站弹出窗口后重试')
  }

  const revokeSoon = () => {
    try {
      URL.revokeObjectURL(blobUrl)
    } catch {
      /* ignore */
    }
  }

  const schedulePrint = () => {
    waitForImagesThenPrint(w)
    w.addEventListener('afterprint', revokeSoon, { once: true })
    setTimeout(revokeSoon, 120_000)
  }

  if (w.document.readyState === 'complete') {
    setTimeout(schedulePrint, 100)
  } else {
    w.addEventListener('load', () => setTimeout(schedulePrint, 50), { once: true })
  }
}

export function printPurchaseOrderByContentId(): void {
  const el = document.getElementById('purchase-pdf-content')
  if (!el) {
    throw new Error('打印内容未就绪，请稍后重试')
  }
  const pages = el.querySelectorAll('.purchase-pdf-page')
  if (pages.length === 0) {
    throw new Error('采购单分页尚未渲染，请稍候再点打印或重新打开预览')
  }
  printPurchaseOrderFromContentElement(el)
}

/**
 * 克隆 #acceptance-pdf-content 到新窗口并唤起系统打印对话框。
 */
export function printAcceptanceOrderFromContentElement(source: HTMLElement): void {
  const clone = source.cloneNode(true) as HTMLElement
  absolutizeImgSrc(clone)

  const bodyHtml = escapeRawHtmlForDocument(clone.outerHTML)
  if (!bodyHtml.trim() || bodyHtml.length < 50) {
    throw new Error('打印区域为空，请关闭后重新打开预览再试')
  }

  const inheritedStyles = collectCurrentDocumentStyles()
  const fullHtml =
    '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    '<title>采购验收单打印</title>' +
    inheritedStyles +
    `<style>${ACCEPTANCE_PRINT_CSS}</style></head><body>${bodyHtml}</body></html>`

  const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' })
  const blobUrl = URL.createObjectURL(blob)

  const w = window.open(blobUrl, '_blank', 'popup=yes,width=960,height=1200')
  if (!w) {
    URL.revokeObjectURL(blobUrl)
    throw new Error('无法打开打印窗口，请允许本站弹出窗口后重试')
  }

  const revokeSoon = () => {
    try {
      URL.revokeObjectURL(blobUrl)
    } catch {
      /* ignore */
    }
  }

  const schedulePrint = () => {
    waitForImagesThenPrint(w)
    w.addEventListener('afterprint', revokeSoon, { once: true })
    setTimeout(revokeSoon, 120_000)
  }

  if (w.document.readyState === 'complete') {
    setTimeout(schedulePrint, 100)
  } else {
    w.addEventListener('load', () => setTimeout(schedulePrint, 50), { once: true })
  }
}

export function printAcceptanceOrderByContentId(): void {
  const el = document.getElementById('acceptance-pdf-content')
  if (!el) {
    throw new Error('打印内容未就绪，请稍后重试')
  }
  const pages = el.querySelectorAll('.acceptance-pdf-page')
  if (pages.length === 0) {
    throw new Error('验收单分页尚未渲染，请稍候再点打印或重新打开预览')
  }
  printAcceptanceOrderFromContentElement(el)
}

/**
 * 横向A4列表打印：克隆指定元素到新窗口并唤起系统打印对话框。
 * @param elementId 打印内容元素ID
 * @param cssExtra 额外的CSS样式字符串（可选）
 */
export function printLandscapeListByElementId(elementId: string, cssExtra?: string): void {
  const el = document.getElementById(elementId)
  if (!el) {
    throw new Error('打印内容未就绪，请稍后重试')
  }
  const clone = el.cloneNode(true) as HTMLElement
  absolutizeImgSrc(clone)

  const bodyHtml = escapeRawHtmlForDocument(clone.outerHTML)
  if (!bodyHtml.trim() || bodyHtml.length < 50) {
    throw new Error('打印区域为空')
  }

  const inheritedStyles = collectCurrentDocumentStyles()
  const fullHtml =
    '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    '<title>列表打印</title>' +
    inheritedStyles +
    `<style>${LANDSCAPE_PRINT_CSS}${cssExtra || ''}</style></head><body>${bodyHtml}</body></html>`

  const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' })
  const blobUrl = URL.createObjectURL(blob)

  const w = window.open(blobUrl, '_blank', 'popup=yes,width=1200,height=800')
  if (!w) {
    URL.revokeObjectURL(blobUrl)
    throw new Error('无法打开打印窗口，请允许本站弹出窗口后重试')
  }

  const revokeSoon = () => {
    try {
      URL.revokeObjectURL(blobUrl)
    } catch {
      /* ignore */
    }
  }

  const schedulePrint = () => {
    waitForImagesThenPrint(w)
    w.addEventListener('afterprint', revokeSoon, { once: true })
    setTimeout(revokeSoon, 120_000)
  }

  if (w.document.readyState === 'complete') {
    setTimeout(schedulePrint, 100)
  } else {
    w.addEventListener('load', () => setTimeout(schedulePrint, 50), { once: true })
  }
}
